/**
 * `/activity` — the shop-wide production activity feed (production-records R2): every recorded
 * press, settle, produce, pack, shelf pull, reprint, ship moment, and every UNDO, across every
 * order, newest first. The daily review lane — "what happened in the shop today, and who did it".
 *
 * Reads `GET /orders/activity` (the PROD_EVENTS partition) a page at a time with a manual "Load
 * older" (the /batches pattern); type/store filters are client-side lenses over the loaded pages —
 * they narrow what is shown, they don't re-query. Live-updates via the change feed ('activity' is
 * registered under the orders domain — event rows publish it for free).
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { GARMENT_TONE_CLASS, orderCardSearch } from './order-detail-lib';
import {
  type ActivityEvent,
  EVENT_LOG_EPOCH_LABEL,
  type EventGroup,
  eventGroup,
  eventLabel,
  eventTone,
  groupEventsByDay,
} from './production-event-lib';

const PAGE_LIMIT = 50;

const GROUP_OPTIONS: ReadonlyArray<{ value: EventGroup | 'all'; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'press', label: 'Presses' },
  { value: 'done', label: 'Completions' },
  { value: 'undo', label: 'Undos' },
  { value: 'reprint', label: 'Reprints' },
  { value: 'shipped', label: 'Ship moments' },
];

export function ActivityPage() {
  const client = useApiClient();
  const [group, setGroup] = useState<string>('all');
  const [store, setStore] = useState<string>('all');

  const listQuery = useInfiniteQuery({
    queryKey: ['activity'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/orders/activity', {
        params: { query: { limit: PAGE_LIMIT, cursor: pageParam } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the activity feed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const loaded: ActivityEvent[] = listQuery.data?.pages.flatMap((p) => p.events) ?? [];
  const storeOptions = [...new Set(loaded.map((e) => e.storeId))].sort();
  const visible = loaded.filter(
    (e) =>
      (store === 'all' || e.storeId === store) && (group === 'all' || eventGroup(e.type) === group),
  );
  const days = groupEventsByDay(visible);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {storeOptions.length > 1 && (
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {storeOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {listQuery.isSuccess && (
          <span className="text-sm text-muted-foreground">
            {visible.length} of {loaded.length} loaded events
          </span>
        )}
      </div>

      {listQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load the activity feed</AlertTitle>
          <AlertDescription>
            {listQuery.error instanceof Error ? listQuery.error.message : 'Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {listQuery.isPending && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {listQuery.isSuccess && visible.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          <Activity className="size-8" />
          <p className="max-w-md text-sm">
            {loaded.length === 0
              ? `No recorded activity yet. The event log began ${EVENT_LOG_EPOCH_LABEL} — every press, settle, produce, pack, pull, ship, and undo lands here from now on.`
              : 'Nothing matches these filters in the loaded pages — load older or widen the filter.'}
          </p>
        </div>
      )}

      {days.map(({ day, events }) => (
        <section key={day} className="space-y-1">
          <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {new Date(events[0]?.at ?? day).toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </h2>
          <ol className="divide-y rounded-lg border">
            {events.map((e) => (
              <ActivityRow key={e.id} event={e} />
            ))}
          </ol>
        </section>
      ))}

      {listQuery.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => listQuery.fetchNextPage()}
          disabled={listQuery.isFetchingNextPage}
        >
          {listQuery.isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : null}
          Load older activity
        </Button>
      )}
    </div>
  );
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-4 py-1.5 text-sm">
      <span
        className="w-16 shrink-0 text-xs tabular-nums text-muted-foreground"
        title={new Date(event.at).toLocaleString()}
      >
        {new Date(event.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
      </span>
      <Badge variant="outline" className={GARMENT_TONE_CLASS[eventTone(event.type)]}>
        {eventLabel(event)}
      </Badge>
      <Link
        to="/orders/$storeId/$orderId"
        params={{ storeId: event.storeId, orderId: event.orderId }}
        search={orderCardSearch('activity')}
        className="font-medium underline-offset-2 hover:underline"
      >
        {event.orderName ?? event.orderId}
      </Link>
      <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
        {event.storeId}
      </span>
      {event.batchId && (
        <Link
          to="/batches"
          search={{ batch: event.batchId }}
          className="text-xs underline underline-offset-2 hover:no-underline"
        >
          batch ↗
        </Link>
      )}
      <span className="ml-auto text-xs text-muted-foreground">{event.actor}</span>
    </li>
  );
}
